import { useState } from 'react'
import type { EntryFilters } from '../hooks/useApi'
import { useT } from '../i18n/context'
import { formatMonthYear, fromLocalInputValue } from '../lib/format'
import { useActiveWorkspaceId } from '../stores/workspace'
import {
    EntriesCsvButton,
    EntriesTable,
    EntryFilterSelects,
} from './EntriesList'
import { Button } from './ui/Button'
import { Card, CardBody, CardHeader } from './ui/Card'

const pad2 = (n: number) => String(n).padStart(2, '0')

/**
 * Einträge-Karte der Zeiterfassung (unter „Zeitraum manuell erfassen"):
 * zeigt den aktuellen Monat, vergangene Monate sind über ‹/› erreichbar.
 */
export function MonthEntriesCard() {
    const t = useT()
    const workspaceId = useActiveWorkspaceId()
    const now = new Date()
    const [year, setYear] = useState(now.getFullYear())
    const [month, setMonth] = useState(now.getMonth() + 1)
    const [filterState, setFilterState] = useState<{
        projectId?: number
        clientId?: number
        entryType?: EntryFilters['entryType']
        page?: number
    }>({ page: 1 })

    const patch = (p: Partial<EntryFilters>) =>
        setFilterState(f => ({ ...f, page: 1, ...p }))

    const prevMonth = () => {
        if (month === 1) {
            setMonth(12)
            setYear(y => y - 1)
        } else {
            setMonth(m => m - 1)
        }
    }
    const nextMonth = () => {
        if (month === 12) {
            setMonth(1)
            setYear(y => y + 1)
        } else {
            setMonth(m => m + 1)
        }
    }
    const isCurrentMonth =
        year === now.getFullYear() && month === now.getMonth() + 1
    const goCurrentMonth = () => {
        const d = new Date()
        setYear(d.getFullYear())
        setMonth(d.getMonth() + 1)
    }

    // Monatsgrenzen als Filter (gleiche Query wie Abrechnung → Einträge)
    const monthEndDay = new Date(year, month, 0).getDate()
    const filters: EntryFilters = {
        ...filterState,
        startDate: fromLocalInputValue(`${year}-${pad2(month)}-01T00:00`),
        endDate: fromLocalInputValue(
            `${year}-${pad2(month)}-${pad2(monthEndDay)}T23:59`,
        ),
    }

    if (workspaceId === null) return null

    return (
        <Card>
            <CardHeader
                title={t('nav.entries')}
                actions={
                    <div className='flex flex-wrap items-center gap-2'>
                        {!isCurrentMonth && (
                            <Button
                                variant='ghost'
                                size='sm'
                                onClick={goCurrentMonth}
                            >
                                {t('track.today')}
                            </Button>
                        )}
                        <div className='flex items-center gap-1'>
                            <Button
                                variant='secondary'
                                size='sm'
                                onClick={prevMonth}
                                aria-label={t('workingTime.prevMonth')}
                            >
                                ‹
                            </Button>
                            <div className='min-w-32 text-center text-sm font-medium text-slate-700 dark:text-slate-200'>
                                {formatMonthYear(year, month)}
                            </div>
                            <Button
                                variant='secondary'
                                size='sm'
                                onClick={nextMonth}
                                aria-label={t('workingTime.nextMonth')}
                            >
                                ›
                            </Button>
                        </div>
                        <EntriesCsvButton
                            workspaceId={workspaceId}
                            filters={filters}
                        />
                    </div>
                }
            />
            <CardBody className='space-y-3'>
                <div className='flex flex-wrap items-end gap-2'>
                    <EntryFilterSelects
                        workspaceId={workspaceId}
                        filters={filterState}
                        onPatch={patch}
                    />
                </div>
                <EntriesTable
                    workspaceId={workspaceId}
                    filters={filters}
                    onPage={page =>
                        setFilterState(f => ({ ...f, page }))
                    }
                />
            </CardBody>
        </Card>
    )
}
